import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Fragment, useEffect, useState, type MouseEvent, type ReactNode } from 'react'
import {
  easeedeskSettingsMenuQuery,
  type EaseedeskMenuEntry,
  type EaseedeskMenuGroup,
} from '../api/easeedeskSettingsMenu'
import './EaseedeskSettingsMenu.css'

/**
 * Meniul din stanga setarilor Easeedesk.
 *
 * Perechea veche: `application/views/version-3/easeedesk_settings/index.php` -
 * sase capitole, cincisprezece capete de submeniu si saptezeci si una de
 * intrari, pe doua niveluri.
 *
 * In v4 lipsea CU TOTUL. Cele sasezeci de ecrane de setari Easeedesk fusesera
 * mutate si se deschideau fiecare cum trebuie, dar intre ele nu era niciun
 * drum: de pe „Ticket types" nu se putea ajunge la „SLA" sau la „Workflows"
 * decat scriind adresa de mana. Portalul vechi, care le tinea pe toate intr-o
 * lista, a fost scos de pe server.
 *
 * -----------------------------------------------------------------------------
 * UNDE STA, SI DE CE NU SE BATE CU RAIL-UL ZONEI
 * -----------------------------------------------------------------------------
 *
 * `settings/easeedesk/...` NU e intr-o zona de setari: `SettingsMenu::items()`
 * n-are niciun prefix `settings/easeedesk`, deci `SettingsRail::zoneFor()`
 * intoarce sirul gol si `SettingsZone` nu deseneaza nimic acolo. Coloana din
 * stanga e libera, si meniul asta o ia intreg - verificat, nu presupus.
 *
 * De-aia si invelisul e altul (`od-module-body`, nu `od-zone-body`): cele doua
 * nu se pot suprapune fiindca nu apar niciodata pe aceeasi adresa.
 *
 * -----------------------------------------------------------------------------
 * NU SE STRANGE LA ICONITE
 * -----------------------------------------------------------------------------
 *
 * Rail-ul zonei sta la 64px si se desface la maus (`settings_console.css`,
 * sectiunea 21). Aici nu se poate: un arbore de doua niveluri nu se poate spune
 * din iconite - randurile dinauntrul unui submeniu n-au niciuna - exact
 * argumentul pentru care nici sub-rail-ul alarmelor nu se strange (sectiunea
 * 17). Clasa `od-module-rail` il tine la 270.
 *
 * -----------------------------------------------------------------------------
 * DREPTURILE NU SE JUDECA AICI
 * -----------------------------------------------------------------------------
 *
 * Lista vine deja scurtata de la `App\Settings\Easeedesk\EaseedeskMenu`: cele
 * saptezeci de `canAccess()` din macheta veche se intreaba pe server, si tot
 * acolo se taie intrarile ale caror ecrane nu s-au rescris inca. Aici se
 * DESENEAZA ce a venit, si atat.
 *
 * -----------------------------------------------------------------------------
 * CLASELE SUNT ALE PORTALULUI, DINADINS
 * -----------------------------------------------------------------------------
 *
 * `od-dark-sidebar-nav`, `menu-header`, `submenu-header`, `menu-tab`: aceleasi
 * ca in macheta veche, deci toata asezarea vine gata din `_sidebar.scss` si din
 * `settings_console.css` - pana la sageata capului de submeniu, care se
 * intoarce singura cand `<li>` primeste `active`.
 *
 * Rail-ul ZONEI si-a schimbat clasa in `od-zone-nav` ca sa nu treaca prin
 * ascultatorul din `public/js/app/app.js`, care da `preventDefault()` inainte
 * sa se uite ce s-a apasat. Motivul ala nu e si al nostru: rail-ul zonei se
 * deseneaza SI in portal, unde bundle-ul ala chiar ruleaza, pe cand meniul asta
 * traieste numai in v4, care nu incarca niciun JavaScript al portalului.
 */

function Entry({ entry }: { entry: EaseedeskMenuEntry }) {
  const navigate = useNavigate()

  /* Legatura ramane o legatura adevarata: are `href`, deci se poate deschide in
     fila noua sau copia. Doar apasarea simpla, cu stanga, se opreste si trece
     prin router - altfel fiecare rand ar reincarca aplicatia intreaga. */
  const go = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }
    event.preventDefault()
    void navigate({ to: '/' + entry.url })
  }

  return (
    <li className={entry.active ? 'menu-tab selected' : 'menu-tab'}>
      <a href={'/app/' + entry.url} onClick={go} title={entry.label}>
        {entry.icon !== '' ? <i className={entry.icon} aria-hidden="true"></i> : null}
        {entry.beta ? <label className="label label-danger">beta</label> : null}
        {entry.label}
      </a>
    </li>
  )
}

function Group({ group, open, onToggle }: { group: EaseedeskMenuGroup; open: boolean; onToggle(): void }) {
  return (
    <li className={open ? 'submenu-header active' : 'submenu-header'}>
      <a
        href="#"
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault()
          onToggle()
        }}
      >
        <i className={group.icon} aria-hidden="true"></i> {group.label}
      </a>
      {/* Ca in macheta: lista sta in pagina si se ascunde cu `display`, fiindca
          `_sidebar.scss` ii da `display: none` oricarui `ul` dinauntru. */}
      <ul style={{ display: open ? 'block' : 'none' }}>
        {group.items.map((entry) => (
          <Entry entry={entry} key={entry.url} />
        ))}
      </ul>
    </li>
  )
}

export function EaseedeskSettingsMenu({ path, children }: { path: string; children: ReactNode }) {
  /* Se intreaba pe orice adresa de setari - scheletul monteaza componenta doar
     acolo - fiindca „e a mea?" nu se poate raspunde dintr-un prefix: doua
     intrari ies din arborele lor (vezi `owns` in api). Raspunsul sta in cache
     cinci minute pe adresa, ca si rail-ul zonei. */
  const menu = useQuery(easeedeskSettingsMenuQuery(path))

  /*
   * Ce a deschis omul cu mana, peste ce a spus serverul.
   *
   * Serverul deschide capul sub care sta randul aprins; restul stau stranse.
   * Cand omul deschide altul, alegerea lui tine pana la urmatoarea mutare -
   * atunci se sterge, ca sa nu ramana deschis un cap dintr-o pagina de mai
   * demult peste cel al paginii pe care tocmai a intrat.
   */
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const active = menu.data?.active ?? ''
  useEffect(() => setOpened({}), [active])

  if (!menu.data || !menu.data.owns || menu.data.sections.length === 0) {
    return <>{children}</>
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {menu.data.sections.map((section) => (
            <Fragment key={section.label}>
              <li className="menu-header">{section.label}</li>
              {section.items.map((item) =>
                item.kind === 'group' ? (
                  <Group
                    group={item}
                    key={section.label + '/' + item.label}
                    open={opened[section.label + '/' + item.label] ?? item.open}
                    onToggle={() =>
                      setOpened((was) => {
                        const key = section.label + '/' + item.label
                        return { ...was, [key]: !(was[key] ?? item.open) }
                      })
                    }
                  />
                ) : (
                  <Entry entry={item} key={item.url} />
                ),
              )}
            </Fragment>
          ))}
        </ul>
      </aside>
      <div className="od-module-body">{children}</div>
    </>
  )
}
